import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import MediaPlayer from './MediaPlayer';
import FilePreview from './FilePreview';

const FileCard = ({ filedata, onPreview }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const { blurNsfw } = useSettings();
    const openPreview = () => onPreview ? onPreview(filedata) : setIsPreviewOpen(true);

    const fileId = filedata.id;
    const userId = filedata.user ? filedata.user.id : "Unknown";
    const username = filedata.user ? filedata.user.username : "Unknown";
    const thumbnailUrl = filedata.thumbnailUrl || filedata.url;
    const name = filedata.name;
    const isSensitive = filedata.isSensitive;
    const host = filedata.user ? filedata.user.host : (filedata.host || null);
    const type = filedata.type;
    const createdAt = new Date(filedata.createdAt).toLocaleDateString();

    const instanceUrl = (localStorage.getItem('instanceUrl') || 'https://misskey.io').replace(/\/+$/,'');
    const fileIdURL = `${instanceUrl}/admin/file/${fileId}`;
    const userPage = `${instanceUrl}/admin/user/${userId}`;

    const isVideo = type.includes('video');
    const isAudio = type.includes('audio');

    const typeLabel = type.split('/')[0].toUpperCase();

    const shouldBlur = isSensitive && blurNsfw;

    const cardClass = `group flex flex-col focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md ${isSensitive ? 'ring-2 ring-amber-400 ring-inset' : ''}`;

    const renderMedia = () => {
        if (isVideo && isPlaying) {
            return <MediaPlayer file={filedata} className="w-full aspect-[4/3] min-h-32 object-contain bg-black" />;
        }
        if (isAudio && isPlaying) {
            return (
                <div className="flex aspect-[4/3] min-h-32 flex-col items-center justify-center bg-gray-900 p-3">
                    <div className="w-10 h-10 shrink-0 bg-blue-500 rounded-full flex items-center justify-center mb-2 shadow-lg shadow-blue-500/50">
                        <svg className="w-8 h-8 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                        </svg>
                    </div>
                    <MediaPlayer file={filedata} className="w-full h-10" />
                </div>
            );
        }

        return (
            <button
                type="button"
                aria-label={(isVideo || isAudio ? '再生: ' : 'プレビュー: ') + name}
                className="relative w-full aspect-[4/3] min-h-32 overflow-hidden bg-gray-100 cursor-pointer flex items-center justify-center focus-visible:outline-blue-600"
                onClick={() => (isVideo || isAudio) ? setIsPlaying(true) : openPreview()}
            >
                {isAudio && !filedata.thumbnailUrl ? (
                    <div className="flex flex-col items-center text-gray-400">
                        <svg className="w-16 h-16 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                        </svg>
                        <span className="text-[10px] uppercase font-bold tracking-wider">Audio File</span>
                    </div>
                ) : (
                    <img
                        src={thumbnailUrl}
                        alt={name}
                        className={`w-full h-full object-cover transition-transform duration-500 ${shouldBlur ? 'blur-md hover:blur-none scale-100 group-hover:scale-110' : 'group-hover:scale-110'}`}
                    />
                )}

                {/* Overlay Badges */}
                <div className="absolute top-2 left-2 flex gap-1">
                    <span className={`px-2 py-0.5 text-[10px] font-bold text-white rounded backdrop-blur-sm ${isAudio ? 'bg-purple-600/80' : 'bg-black/60'}`}>
                        {typeLabel}
                    </span>
                    {isSensitive && (
                        <span className="px-2 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded shadow-sm">
                            NSFW
                        </span>
                    )}
                </div>

                {/* Play Button Overlay */}
                {(isVideo || isAudio) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-white/90 rounded-full p-3 shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
                            <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                            </svg>
                        </div>
                    </div>
                )}
            </button>
        );
    };

    return (
        <div className={cardClass} data-file-id={fileId} tabIndex={0} role="group" aria-label={'File ' + fileId + ': ' + name}>
            {renderMedia()}

            <div className="p-3 flex flex-col flex-grow">
                <div className="flex items-start gap-2 mb-1">
                    <h3 className="min-w-0 flex-1 font-medium text-gray-900 text-sm truncate" title={name}>{name}</h3>
                    <button type="button" onClick={openPreview} aria-label={'拡大プレビュー: ' + name} title="拡大プレビュー" className="shrink-0 rounded p-1 text-gray-500 hover:bg-blue-50 hover:text-blue-600 focus-visible:outline-blue-600">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5" />
                        </svg>
                    </button>
                </div>
                <p className="text-gray-500 text-[11px] mb-3">
                    {createdAt}
                </p>

                <div className="mt-auto space-y-1.5 border-t border-gray-100 pt-3">
                    <div className="flex items-center text-xs text-gray-600">
                        <svg className="w-3.5 h-3.5 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <a href={userPage} target="_blank" rel="noopener noreferrer" className="truncate hover:text-blue-600 transition-colors">
                            {username}
                        </a>
                    </div>

                    {host && (
                        <div className="flex items-center text-xs text-gray-600">
                            <svg className="w-3.5 h-3.5 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-7 0V4" />
                            </svg>
                            <Link to={'/servers/' + encodeURIComponent(host) + '/files'} target="_blank" rel="noopener noreferrer" className="truncate hover:text-blue-600 transition-colors">
                                {host}
                            </Link>
                        </div>
                    )}

                    <div className="flex items-center text-xs text-gray-600">
                        <svg className="w-3.5 h-3.5 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <a href={fileIdURL} target="_blank" rel="noopener noreferrer" className="truncate hover:text-blue-600 transition-colors">
                            Admin File Link
                        </a>
                    </div>
                </div>
            </div>
            {isPreviewOpen && <FilePreview file={filedata} onClose={() => setIsPreviewOpen(false)} />}
        </div>
    );
};

export default FileCard;
